import React, { useEffect, useState } from 'react';
import { api as axios } from '../utils/apiClient';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import './AddWebsite.css';
import { getStoredProfile } from '../utils/profileStorage';

const buildInitialFormState = () => {
  const profile = getStoredProfile();
  return {
    name: '',
    url: '',
    ownerEmail: profile.email || '',
    authMethod: 'application_password', // 'application_password', 'api_token', 'plugin_key', 'basic_auth'
    // Application Password
    wpAppUsername: '',
    wpAppPassword: '',
    // API Token
    apiToken: '',
    // Plugin Key
    pluginKey: '',
    // Basic Auth
    basicAuthUsername: '',
    basicAuthPassword: ''
  };
};

const AddWebsite = () => {
  const [formData, setFormData] = useState(buildInitialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [siteType, setSiteType] = useState('wordpress'); // 'wordpress'

  useEffect(() => {
    const syncProfileEmail = () => {
      const profile = getStoredProfile();
      setFormData((prev) => ({ ...prev, ownerEmail: profile.email || '' }));
    };
    window.addEventListener('profile-updated', syncProfileEmail);
    return () => window.removeEventListener('profile-updated', syncProfileEmail);
  }, []);

  const validateUrl = (url) => {
    try {
      const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
      // eslint-disable-next-line no-new
      new URL(formattedUrl);
      return formattedUrl;
    } catch {
      return null;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const testConnection = async () => {
    const validatedUrl = validateUrl(formData.url.trim());
    if (!validatedUrl) {
      setMessage({ type: 'error', text: 'Please enter a valid URL first.' });
      return;
    }

    setTestingConnection(true);
    setMessage(null);
    
    try {
      // Test the health endpoint
      const healthUrl = `${validatedUrl.replace(/\/$/, '')}/wp-json/webmonitor/v1/health`;
      const response = await axios.get(healthUrl, { timeout: 10000 });
      
      if (response.data && response.data.plugin_installed) {
        setMessage({ 
          type: 'success', 
          text: '✅ Connection successful! WebMonitor plugin is installed and active.' 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: '⚠️ Plugin endpoint not found. Please install the WebMonitor WordPress plugin first.' 
        });
      }
    } catch (error) {
      if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        setMessage({ 
          type: 'error', 
          text: '❌ Cannot connect to the website. Check if the URL is correct and the site is accessible.' 
        });
      } else if (error.response?.status === 404) {
        setMessage({ 
          type: 'error', 
          text: '⚠️ WebMonitor plugin not found. Please install the WordPress plugin first.' 
        });
      } else {
        setMessage({ 
          type: 'error', 
          text: `Connection test failed: ${error.message}` 
        });
      }
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Please provide a website name.' });
      return;
    }

    const validatedUrl = validateUrl(formData.url.trim());
    if (!validatedUrl) {
      setMessage({ type: 'error', text: 'Enter a valid URL (e.g. https://example.com).' });
      return;
    }

    if (!validateEmail(formData.ownerEmail.trim())) {
      setMessage({ type: 'error', text: 'Provide a valid owner email for alerts.' });
      return;
    }

    setSubmitting(true);
    try {
      // Prepare data based on auth method
      const siteData = {
        name: formData.name.trim(),
        url: validatedUrl,
        ownerEmail: formData.ownerEmail.trim(),
        authMethod: formData.authMethod
      };

      // Helper function to sanitize credential values (prevent undefined/null/empty)
      const sanitizeCred = (value) => {
        if (!value || value === undefined || value === null || String(value).trim() === '') {
          return undefined; // Don't send undefined values
        }
        return String(value).trim();
      };

      // Add credentials based on selected auth method - only include if they have values
      if (formData.authMethod === 'application_password') {
        const username = sanitizeCred(formData.wpAppUsername);
        const password = sanitizeCred(formData.wpAppPassword?.replace(/\s+/g, ''));
        if (username) siteData.wpAppUsername = username;
        if (password) siteData.wpAppPassword = password;
      } else if (formData.authMethod === 'api_token') {
        const token = sanitizeCred(formData.apiToken);
        if (token) siteData.apiToken = token;
      } else if (formData.authMethod === 'plugin_key') {
        const key = sanitizeCred(formData.pluginKey);
        if (key) siteData.pluginKey = key;
      } else if (formData.authMethod === 'basic_auth') {
        const username = sanitizeCred(formData.basicAuthUsername);
        const password = sanitizeCred(formData.basicAuthPassword);
        if (username) siteData.basicAuthUsername = username;
        if (password) siteData.basicAuthPassword = password;
      }

      const response = await axios.post('/api/sites', siteData);
      setMessage({ type: 'success', text: 'Website added successfully! Plugins will sync automatically.' });
      setFormData(buildInitialFormState);
      
      // Optionally test connection
      if (response.data._id) {
        setTimeout(() => {
          // Navigate to dashboard or show success with link
        }, 1500);
      }
    } catch (error) {
      const errorText = error.response?.data?.message || 'Unable to save website. Please try again.';
      setMessage({ type: 'error', text: errorText });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard-container add-website-page">
      <Sidebar />
      <div className="main-content">
        <div className="header add-website-header">
          <div>
            <p className="eyebrow">Sites / Add Website</p>
            <h1>Add a New Website</h1>
            <p className="subheading">
              Connect a WordPress site to monitor uptime, performance, plugins, and security from a single dashboard.
            </p>
          </div>
          <Link to="/dashboard" className="ghost-link">
            ← Back to Dashboard
          </Link>
        </div>

        <form className="add-website-form" onSubmit={handleSubmit}>
          <div className="form-card">
            <h2>Site Information</h2>
            <div className="form-grid">
              <label>
                Website Name<span className="required">*</span>
                <input
                  type="text"
                  name="name"
                  placeholder="e.g. Acme Marketing"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </label>

              <label>
                Website URL<span className="required">*</span>
                <input
                  type="text"
                  name="url"
                  placeholder="https://acme.com"
                  value={formData.url}
                  onChange={handleInputChange}
                />
              </label>

              <label>
                Owner / Alert Email<span className="required">*</span>
                <input
                  type="email"
                  name="ownerEmail"
                  placeholder="ops-team@example.com"
                  value={formData.ownerEmail}
                  onChange={handleInputChange}
                />
              </label>

              <label className="site-type-field">
                Website Type<span className="required">*</span>
                <select
                  value={siteType}
                  onChange={(e) => setSiteType(e.target.value)}
                >
                  <option value="wordpress">WordPress website</option>
                </select>
              </label>
            </div>
          </div>

          <div className="form-card">
            <div className="form-card-header">
              <div>
                <h2>WordPress</h2>
                <p>Choose an authentication method to sync plugins, updates, and security checks in real-time.</p>
              </div>
            </div>

            <div className="auth-method-selector">
              <label className="auth-method-label">
                <span>Authentication Method:</span>
                <select
                  name="authMethod"
                  value={formData.authMethod}
                  onChange={handleInputChange}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid #d9d4cf',
                    fontSize: '1rem',
                    width: '100%',
                    maxWidth: '400px'
                  }}
                >
                  <option value="application_password">Application Password (Recommended)</option>
                  <option value="api_token">API Token</option>
                  <option value="plugin_key">WordPress Plugin Key</option>
                  <option value="basic_auth">Basic Authentication</option>
                  <option value="none">No Authentication (Test Endpoint)</option>
                </select>
              </label>
            </div>

            {/* Application Password Fields */}
            {formData.authMethod === 'application_password' && (
              <div className="auth-fields">
                <div className="form-grid">
                  <label>
                    WordPress Admin Username
                    <input
                      type="text"
                      name="wpAppUsername"
                      placeholder="wordpress-admin"
                      value={formData.wpAppUsername}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label>
                    Application Password
                    <input
                      type="password"
                      name="wpAppPassword"
                      placeholder="••••••••••••"
                      value={formData.wpAppPassword}
                      onChange={handleInputChange}
                    />
                  </label>
                </div>
                <p style={{ fontSize: '0.9em', color: '#5b6472', marginTop: '10px' }}>
                  Create an Application Password in WordPress: <strong>Users → Profile → Application Passwords</strong>
                </p>
              </div>
            )}

            {/* API Token Fields */}
            {formData.authMethod === 'api_token' && (
              <div className="auth-fields">
                <div className="form-grid">
                  <label>
                    API Token
                    <input
                      type="password"
                      name="apiToken"
                      placeholder="Enter your API token"
                      value={formData.apiToken}
                      onChange={handleInputChange}
                    />
                  </label>
                </div>
                <p style={{ fontSize: '0.9em', color: '#5b6472', marginTop: '10px' }}>
                  Use your WordPress REST API token for authentication. Set this in your WordPress settings or via a plugin.
                </p>
              </div>
            )}

            {/* Plugin Key Fields */}
            {formData.authMethod === 'plugin_key' && (
              <div className="auth-fields">
                <div className="form-grid">
                  <label>
                    Plugin Key / Secret
                    <input
                      type="password"
                      name="pluginKey"
                      placeholder="Enter your plugin key"
                      value={formData.pluginKey}
                      onChange={handleInputChange}
                    />
                  </label>
                </div>
                <p style={{ fontSize: '0.9em', color: '#5b6472', marginTop: '10px' }}>
                  Use the key provided by the WebMonitor WordPress plugin. Install the plugin first to get your key.
                </p>
              </div>
            )}

            {/* Basic Auth Fields */}
            {formData.authMethod === 'basic_auth' && (
              <div className="auth-fields">
                <div className="form-grid">
                  <label>
                    Username
                    <input
                      type="text"
                      name="basicAuthUsername"
                      placeholder="username"
                      value={formData.basicAuthUsername}
                      onChange={handleInputChange}
                    />
                  </label>
                  <label>
                    Password
                    <input
                      type="password"
                      name="basicAuthPassword"
                      placeholder="password"
                      value={formData.basicAuthPassword}
                      onChange={handleInputChange}
                    />
                  </label>
                </div>
                <p style={{ fontSize: '0.9em', color: '#5b6472', marginTop: '10px' }}>
                  Use HTTP Basic Authentication credentials if your WordPress site requires it.
                </p>
              </div>
            )}

            {/* No Auth Info */}
            {formData.authMethod === 'none' && (
              <div className="auth-fields">
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#fef3c7', 
                  borderRadius: '8px',
                  border: '1px solid #fbbf24'
                }}>
                  <p style={{ margin: 0, color: '#92400e' }}>
                    <strong>Test Endpoint Mode:</strong> This will use the public test endpoint from the WebMonitor WordPress plugin. 
                    Make sure the plugin is installed and the test endpoint is enabled.
                  </p>
                </div>
              </div>
            )}

            {/* Connection Test Button */}
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                type="button"
                onClick={testConnection}
                disabled={testingConnection || !formData.url.trim()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#0f766e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: testingConnection || !formData.url.trim() ? 'not-allowed' : 'pointer',
                  opacity: testingConnection || !formData.url.trim() ? 0.6 : 1,
                  fontSize: '0.95em',
                  fontWeight: 600
                }}
              >
                {testingConnection ? 'Testing...' : '🔍 Test Connection'}
              </button>
              <span style={{ fontSize: '0.85em', color: '#6b7280' }}>
                Test if the WebMonitor plugin is installed on your WordPress site
              </span>
            </div>
          </div>

          {message && (
            <div className={`form-alert ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="form-footer">
            <button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Add Website'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddWebsite;



